import { LiveEvent } from '@/modules/live/types/Live';
import { Vue } from 'vue-class-component';
import RavTable, { TableCellDescription } from '@/components/rav-table/rav-table';
import HumanizedChannelsProp from '@/modules/live/components/humanized-channels-prop';
import { $thesaurus, liveChannelsService, liveSourcesService } from '@/modules/live/live-facade';
import { FieldDescription, FieldDescription$Type } from '@/types/FieldDescription';
import FormatterFork from '@/components/formatter-fork/formatter-fork';
import { STATUSES, AUTO_START } from '@/modules/live/descriptions/live-events';
import { LiveEvent$Status } from '@Ravnur-Inc/shared/types/Live';
import HumanizedDuration from '@/modules/live/components/humanized-duration';
import LiveEventsRepository, {
  LiveEventsQueryParams,
} from '@/modules/live/repositories/live-event-repository';
import PopupButton from '@/components/popup-button/popup-button';
import CreateLiveEventModal from '@/modules/live/components/create-live-event-modal';

import './live-events-table.scss';

const CN = 'live-events-table';

const repository = new LiveEventsRepository();

const DEFAULT_PARAMS: Partial<LiveEventsQueryParams> = {
  sortField: 'startDateTime',
  sortType: 'asc',
  status: [LiveEvent$Status.SCHEDULED, LiveEvent$Status.ON_AIR, LiveEvent$Status.PREVIEW],
};

type DashboardLiveEvent = LiveEvent & {
  channelIdsForPrivacy: string[]; // duplication channelIds for privacy slot
};

type DashboardLiveEventActionOption = {
  label: string;
  process: (event: DashboardLiveEvent) => void;
};

export default class LiveEventsTable extends Vue {
  events: LiveEvent[] = [];
  total = 0;
  loading = false;
  initialCount = 5;
  timeout: Nullable<NodeJS.Timeout> = null;

  get list() {
    return this.events.map((e) => ({
      ...e,
      channelIdsForPrivacy: e.channelIds,
    }));
  }

  get hasMore() {
    return this.total > this.events.length;
  }

  get slots() {
    return {
      startDateTime: this.renderStartTime,
      channelIds: this.renderChannel,
      status: this.renderStatus,
      autoStart: this.renderAutoStart,
      liveSourceId: this.renderSource,
      duration: this.renderDuration,
      channelIdsForPrivacy: this.renderPrivacy,
      actions: this.renderActions,
    };
  }

  render() {
    const descriptions: Array<TableCellDescription<DashboardLiveEvent>> = [
      { key: 'startDateTime', label: this.$t('dashboard-live-table', 'column__start-time') },
      { key: 'status', label: this.$t('dashboard-live-table', 'column__status') },
      { key: 'title', label: this.$t('dashboard-live-table', 'column__title') },
      { key: 'channelIds', label: this.$t('dashboard-live-table', 'column__channel') },
      { key: 'autoStart', label: this.$t('dashboard-live-table', 'column__auto-start') },
      { key: 'liveSourceId', label: this.$t('dashboard-live-table', 'column__source') },
      { key: 'duration', label: this.$t('dashboard-live-table', 'column__duration') },
      { key: 'channelIdsForPrivacy', label: this.$t('dashboard-live-table', 'column__privacy') },
      { key: 'actions', label: this.$t('dashboard-live-table', 'column__actions') },
    ];

    return (
      <card
        class={CN}
        title="Current and upcoming live events"
        v-slots={{ header: this.renderCustomCreateBtn }}
      >
        {this.loading && <spinner />}
        {this.list.length > 0 ? (
          <>
            <div class={`${CN}__table-wrapper`}>
              <RavTable
                class={`${CN}__table`}
                descriptions={descriptions}
                list={this.list}
                mode="striped"
                v-slots={this.slots}
              />
            </div>
            {this.hasMore && (
              <div class={`${CN}__table-footer`}>
                <action-button
                  class={`${CN}__load-more`}
                  color="primary"
                  mode="frameless"
                  process={this.loadMore}
                >
                  {this.$t('dashboard-live-table', 'action__load-more')}
                </action-button>
              </div>
            )}
          </>
        ) : (
          <div class={`${CN}__empty`} v-show={!this.loading}>
            <l10n group="dashboard-live-table" tkey="info__no-events" />
          </div>
        )}
      </card>
    );
  }

  renderActions({ item }: { item: DashboardLiveEvent }) {
    const actions: DashboardLiveEventActionOption[] = [
      {
        label: this.$t('dashboard-live-table', 'action__control-room'),
        process: this.toControlRoom,
      },
    ];

    if (item.status === LiveEvent$Status.SCHEDULED) {
      actions.push({
        label: this.$t('dashboard-live-table', 'action__edit'),
        process: this.toEditPage,
      });
    }

    return (
      <more-actions
        openerPosition="top"
        options={actions}
        onClick={(option: DashboardLiveEventActionOption) => option.process(item)}
      />
    );
  }

  renderPrivacy({ item }: { item: DashboardLiveEvent }) {
    return <HumanizedChannelsProp prop="privacy" value={item.channelIdsForPrivacy} />;
  }

  renderSource({ item }: { item: DashboardLiveEvent }) {
    const description = {
      type: FieldDescription$Type.THESAURUS,
      field: 'liveSourceId',
      thesaurus: liveSourcesService.list.map((s) => ({
        id: s.id,
        label: s.title,
      })),
    };

    return (
      <FormatterFork
        description={description as FieldDescription<Entity>}
        entity={item}
        value={item.liveSourceId}
      />
    );
  }

  renderAutoStart({ item }: { item: DashboardLiveEvent }) {
    const description = {
      type: FieldDescription$Type.STRING,
      thesaurus: $thesaurus(AUTO_START, 'live-events'),
    };

    return (
      <FormatterFork
        description={description as FieldDescription<Entity>}
        entity={item}
        value={item.autoStart}
      />
    );
  }

  renderStartTime({ item }: { item: DashboardLiveEvent }) {
    const description = {
      type: FieldDescription$Type.DATE,
    };

    return (
      <FormatterFork
        description={description as FieldDescription<Entity>}
        entity={item}
        value={item.startDateTime}
      />
    );
  }

  renderStatus({ item }: { item: DashboardLiveEvent }) {
    const description = {
      type: FieldDescription$Type.THESAURUS,
      thesaurus: $thesaurus(
        STATUSES.filter((s) => s.id !== LiveEvent$Status.FINISHED),
        'live-events'
      ),
    };

    return (
      <FormatterFork
        description={description as FieldDescription<Entity>}
        entity={item}
        value={item.status}
      />
    );
  }

  renderDuration({ item }: { item: DashboardLiveEvent }) {
    return <HumanizedDuration value={item.duration} />;
  }

  renderChannel({ item }: { item: DashboardLiveEvent }) {
    return <HumanizedChannelsProp prop="title" value={item.channelIds} />;
  }

  renderCustomCreateBtn() {
    return (
      <PopupButton color="primary" size="sm" text={this.$t('live-events', 'page__add-new-btn')}>
        <CreateLiveEventModal noRedirect onSaved={this.fetchEvents} />
      </PopupButton>
    );
  }

  toEditPage(item: LiveEvent) {
    this.$router.push({
      name: 'LiveEventDetails',
      params: {
        id: item.id,
      },
    });
  }

  toControlRoom(item: LiveEvent) {
    this.$router.push({
      name: 'LiveEventControlRoom',
      params: {
        id: item.id,
      },
    });
  }

  async fetchEvents(params: Nullable<Partial<LiveEventsQueryParams>> = null, silent = false) {
    if (!silent) {
      this.loading = true;
    }

    let queryParams = {
      ...DEFAULT_PARAMS,
      count: this.initialCount,
    };

    if (params) {
      queryParams = {
        ...queryParams,
        ...params,
      };
    }

    try {
      const { items, total } = await repository.load(queryParams);

      this.events = items;
      this.total = total;
    } catch (e) {
      this.$processException(e);
    } finally {
      this.loading = false;
    }
  }

  async loadMore() {
    this.initialCount = this.events.length + 5;
    await this.fetchEvents(null, true);
  }

  async startPooling() {
    await this.fetchEvents();

    this.timeout = setInterval(() => this.fetchEvents(null, true), 5000);
  }

  async mounted() {
    await Promise.all([liveChannelsService.load(), liveSourcesService.load(), this.startPooling()]);
  }

  beforeUnmount() {
    if (this.timeout) {
      clearInterval(this.timeout);
    }
  }
}
